import { isDatalessWidgetType, isReadmodelWidgetType, WidgetDefinition, WidgetType } from '../types';
import { ValidationErrors } from '../validateWidget';
import { getReadmodelCollection, isAlertSeverity, SEVERITY_WIDGET_TYPES } from '../readmodelSource';
import { readKpiActivityMeasure } from './kpiState';
import { TFunction } from 'i18next';

// Common
const validateCommon = ( widgetConfig: WidgetDefinition, t: TFunction ): ValidationErrors | null => {
  const errors: ValidationErrors = {};

  if ( !widgetConfig.name?.trim()) {
    errors.basic = { ...( errors.basic ?? {}), name: t( 'widget_builder_validation_name_required', 'Name is required' ) };
  }

  // TV1-1930 — the second (and last) front-end site that hard-codes "a cube and at
  // least one measure". A dataless widget (`text`) has no data binding at all, so
  // both preconditions are skipped for it. See `DATALESS_WIDGET_TYPES`.
  //
  // TV1-2291 (t25) — a READMODEL widget is skipped for the same reason, and it is
  // not a courtesy: `UiWidgetReadmodelBaseSchema` extends `UiWidgetBaseSchema`, NOT
  // `UiWidgetDataBaseSchema`, so `count` / `alert` / `list` / `progress` have no
  // `cube` and no `measures` FIELDS AT ALL. Demanding them would make every
  // readmodel card permanently invalid — "Data source is required" on a card whose
  // data source is a collection, chosen two fields away. What replaces the check is
  // `validateReadmodelBinding` below, which demands the binding they DO have.
  if ( !isDatalessWidgetType( widgetConfig.type ) && !isReadmodelWidgetType( widgetConfig.type )) {
    if ( !widgetConfig.cube?.trim()) {
      errors.basic = { ...( errors.basic ?? {}), cube: t( 'widget_builder_validation_datasource_required', 'Data source is required' ) };
    }

    if ( !widgetConfig.measures?.length ) {
      errors.basic = { ...( errors.basic ?? {}), measures: t( 'widget_builder_validation_measures_required', 'At least one measure is required' ) };
    }
  }

  if ( !widgetConfig.type ) {
    errors.basic = { ...( errors.basic ?? {}), type: t( 'widget_builder_validation_type_required', 'Widget type is required' ) };
  }

  return Object.keys( errors ).length ? errors : null;
};

// Cartesian Validation
const validateCartesianWidgetProps = ( widgetConfig: WidgetDefinition, t: TFunction ): ValidationErrors | null => {
  const errors: ValidationErrors = {};
  const props = widgetConfig.widgetComponentProps || {};

  if ( !props.xAxis?.trim()) {
    errors.widgetProps = { ...( errors.widgetProps ?? {}), xAxis: t( 'widget_builder_validation_xaxis_required', 'X-Axis is required' ) };
  }
  if ( !props.yAxis?.trim()) {
    errors.widgetProps = { ...( errors.widgetProps ?? {}), yAxis: t( 'widget_builder_validation_yaxis_required', 'Y-Axis is required' ) };
  }
  if ( !props.series?.trim()) {
    errors.widgetProps = { ...( errors.widgetProps ?? {}), series: t( 'widget_builder_validation_series_required', 'Series is required' ) };
  }

  return Object.keys( errors ).length ? errors : null;
};

// Area Chart
const validateAreaChart = ( widgetConfig: WidgetDefinition, t: TFunction ): ValidationErrors | null => {
  const commonErrors = validateCommon( widgetConfig, t ) || {};
  const propsErrors = validateCartesianWidgetProps( widgetConfig, t ) || {};
  const errors: ValidationErrors = { ...commonErrors, ...propsErrors };
  return Object.keys( errors ).length ? errors : null;
};

// Bar Chart
const validateBarChart = ( widgetConfig: WidgetDefinition, t: TFunction ): ValidationErrors | null => {
  const commonErrors = validateCommon( widgetConfig, t ) || {};
  const propsErrors = validateCartesianWidgetProps( widgetConfig, t ) || {};
  const errors: ValidationErrors = { ...commonErrors, ...propsErrors };
  return Object.keys( errors ).length ? errors : null;
};

// Line Chart
const validateLineChart = ( widgetConfig: WidgetDefinition, t: TFunction ): ValidationErrors | null => {
  const commonErrors = validateCommon( widgetConfig, t ) || {};
  const propsErrors = validateCartesianWidgetProps( widgetConfig, t ) || {};
  const errors: ValidationErrors = { ...commonErrors, ...propsErrors };
  return Object.keys( errors ).length ? errors : null;
};

// Pie Chart
const validatePieChart = ( widgetConfig: WidgetDefinition, t: TFunction ): ValidationErrors | null => {
  const commonErrors = validateCommon( widgetConfig, t ) || {};
  const errors: ValidationErrors = { ...commonErrors };
  const props = widgetConfig.widgetComponentProps || {};

  if ( !props.groupBy?.trim()) {
    errors.widgetProps = { ...( errors.widgetProps ?? {}), groupBy: t( 'widget_builder_validation_groupby_required', 'Group by is required' ) };
  }
  if ( !props.valueMetric?.trim()) {
    errors.widgetProps = { ...( errors.widgetProps ?? {}), valueMetric: t( 'widget_builder_validation_valuemetric_required', 'Value metric is required' ) };
  }

  return Object.keys( errors ).length ? errors : null;
};

// Donut Chart
const validateDonutChart = ( widgetConfig: WidgetDefinition, t: TFunction ): ValidationErrors | null => validatePieChart( widgetConfig, t );

// Scatter Chart
const validateScatterChart = ( widgetConfig: WidgetDefinition, t: TFunction ): ValidationErrors | null => {
  const commonErrors = validateCommon( widgetConfig, t ) || {};
  const errors: ValidationErrors = { ...commonErrors };

  const props = widgetConfig.widgetComponentProps || {};

  if ( typeof props.dataKey !== 'object' ) {
    errors.widgetProps = { ...( errors.widgetProps ?? {}), dataKey: t( 'widget_builder_validation_datakey_required', 'dataKey with { x, y } is required' ) };
  } else {
    const { x, y } = props.dataKey as Record<string, unknown>;
    if ( typeof x !== 'string' || !x.trim()) {
      errors.widgetProps = { ...( errors.widgetProps ?? {}), 'dataKey.x': t( 'widget_builder_validation_datakey_x_required', 'dataKey.x must be a non-empty string' ) };
    }
    if ( typeof y !== 'string' || !y.trim()) {
      errors.widgetProps = { ...( errors.widgetProps ?? {}), 'dataKey.y': t( 'widget_builder_validation_datakey_y_required', 'dataKey.y must be a non-empty string' ) };
    }
  }

  if ( !props.groupBy?.trim()) {
    errors.widgetProps = { ...( errors.widgetProps ?? {}), groupBy: t( 'widget_builder_validation_groupby_required', 'Group by is required' ) };
  }

  return Object.keys( errors ).length ? errors : null;
};

// Critical Stock (list) — TV1-1933 (S18). Only the common data-source fields are
// required; the runway threshold is an optional display prop with a default, so
// there is no widget-prop precondition beyond `measures`.
const validateCriticalStock = ( widgetConfig: WidgetDefinition, t: TFunction ): ValidationErrors | null =>
  validateCommon( widgetConfig, t );

// Table/Pivot — TV1-1929 (S14). Every widget-specific prop (`pivotConfig`,
// `visibleColumns`, `showTotals`) is optional with a sane default: with none of
// them set, the widget renders a flat table of the measures/dimensions the common
// fields already require. So there is no precondition beyond `validateCommon`.
const validateTable = ( widgetConfig: WidgetDefinition, t: TFunction ): ValidationErrors | null =>
  validateCommon( widgetConfig, t );

/**
 * Does this KPI declare more measures than it can justify? — TV1-2291 (t29).
 *
 * The KPI is the ONE widget that TIGHTENS `validateCommon` rather than merely
 * reusing it: it is a SINGLE number, so "at least one measure" is not enough. The
 * rule used to be "exactly one measure"; it is now **exactly one DISPLAYED
 * measure**, because there is one — and only one — legitimate reason to query a
 * second column.
 *
 * **Displayed** is `measures[0]`, by construction: `WebWidget.parseKpiData` reads
 * `measures[0]` and nothing else. A second DISPLAYED measure would come back from
 * Cube and be silently dropped, presenting one metric under another's title, so
 * refusing to build it is still the honest behaviour.
 *
 * **The exception is a WITNESS** (`widgetComponentProps.activityMeasure`,
 * TV1-2291): a measure that is queried but never rendered, whose only job is to
 * tell "this window had no activity" apart from "this metric cannot be computed
 * yet" — the discriminator in `kpiState.resolveKpiState`. A witness that is not
 * IN the query is inert (`sumMeasure` finds no column and the state degrades to
 * `basis: 'declaration'`), which is exactly why the rule has to make room for it
 * rather than being dropped.
 *
 * ## The witness must be `measures[1]` — the POSITION, not merely membership
 *
 * - `measures[0]` is the displayed slot by construction, so a definition naming
 *   it as its own witness is nonsense — a value that summed to unknown cannot
 *   witness itself — and a positional rule rejects that for free.
 * - Since a witness legitimises exactly ONE extra column, "in the list but not
 *   displayed" and "is `measures[1]`" describe the same set anyway. Pinning the
 *   index costs nothing and makes the contract mechanical rather than inferred.
 *
 * Deliberately NOT relaxed any further: two measures with no declared witness are
 * still rejected, and so are three measures with one. The rule has to keep
 * catching the mistake it exists to catch — an author who thinks a KPI can show
 * two numbers.
 *
 * Exported because the Mobile twin (`MobileWidgetValidators.validateKpi`) applies
 * the SAME rule — a KPI is a single number on any device — and a hand-copied
 * predicate is the thing that drifts.
 */
export const hasUnjustifiedKpiMeasures = ( widgetConfig: WidgetDefinition ): boolean => {
  const measures = widgetConfig.measures ?? [];
  // The empty case is `validateCommon`'s business; one measure is the plain,
  // witness-less KPI. Neither is this rule's concern.
  if ( measures.length < 2 ) return false;

  // Same reader the RUNTIME uses, so the builder and `resolveKpiState` cannot
  // disagree about what counts as a declared witness (trimmed, non-empty).
  const witness = readKpiActivityMeasure( widgetConfig.widgetComponentProps );

  return !( measures.length === 2 && witness !== null && witness === measures[1].trim());
};

// KPI (big number) — TV1-1930 (S15), relaxed by TV1-2291 (t29).
//
// See {@link hasUnjustifiedKpiMeasures} for the measure rule. Everything else
// (`format`, `currency`, `decimals`, `goodDirection`, `compare`) is an optional
// display prop with a safe default.
const validateKpi = ( widgetConfig: WidgetDefinition, t: TFunction ): ValidationErrors | null => {
  const errors: ValidationErrors = validateCommon( widgetConfig, t ) || {};

  // `validateCommon` already reports the empty case; only the surplus case is new
  // here, so we don't stack two errors on the same field for an empty selection.
  // The copy is unchanged on purpose: a rejected definition is rejected for the
  // same reason it always was, and TV1-2291 ships no new user-facing strings.
  if ( hasUnjustifiedKpiMeasures( widgetConfig )) {
    errors.basic = {
      ...( errors.basic ?? {}),
      measures: t( 'widget_builder_validation_kpi_single_measure', 'A KPI shows exactly one measure' )
    };
  }

  return Object.keys( errors ).length ? errors : null;
};

// Text / Markdown — TV1-1930 (S15). The DATALESS widget.
//
// `validateCommon` already skips the cube/measures preconditions for a dataless
// type, so all that survives is "it needs a name". The content itself is NOT
// required: an empty text block is a legitimate intermediate state while
// authoring, and the widget renders its own empty state for it.
const validateText = ( widgetConfig: WidgetDefinition, t: TFunction ): ValidationErrors | null =>
  validateCommon( widgetConfig, t );

// Heading / Spacer — TV1-2291 (t21). The second and third DATALESS types.
//
// `validateCommon` already skips the cube/measures preconditions for a dataless
// type, so all that survives is "it needs a name". Deliberately NOTHING further:
//
//  - a heading with no `title` is a legitimate intermediate state while authoring
//    (`HeadingWidget` renders its own placeholder for it), exactly as an empty
//    `text` block is. Requiring it would block the only sane authoring order —
//    place the divider, then name the section.
//  - a spacer has no content to validate at all; its size is its whole
//    configuration, and size is not a definition field.
const validateHeading = ( widgetConfig: WidgetDefinition, t: TFunction ): ValidationErrors | null =>
  validateCommon( widgetConfig, t );

const validateSpacer = ( widgetConfig: WidgetDefinition, t: TFunction ): ValidationErrors | null =>
  validateCommon( widgetConfig, t );

/**
 * The READMODEL binding — TV1-2291 (t25). The counterpart of `validateCommon`'s
 * cube/measures preconditions, for the source that has neither.
 *
 * Errors are keyed under `readmodelSource`, the section `WidgetInspector` mounts for
 * a readmodel card, so each one is reported where it can be fixed.
 *
 * Two rules, and BOTH are schema-required fields rather than invented ones:
 *
 *  - **`collection`** is `S.string()` and REQUIRED on `UiWidgetReadmodelBaseSchema`.
 *    A card without one has nothing to fetch; `toReadmodelQuery` throws for it and
 *    the tile becomes an error tile. Rejecting it in the editor is strictly better
 *    than shipping a report with a broken card in it. It must also be a collection
 *    the CATALOG knows — an unknown one throws `UnknownReadmodelCollectionError` at
 *    fetch time, which is loud but, again, only after the report is saved.
 *  - **`severity`**, for the types that declare it (`alert`, today). It is a
 *    REQUIRED enum: an alert is the one tile whose colour a seller reads as a
 *    judgement about their business, so a defaulted severity would assert an urgency
 *    nobody authored. `AlertWidget` still falls back to `'medium'` at render — that
 *    fallback is for a layout that got past this check some other way (a seed, an
 *    older build), not a licence to skip it here.
 *
 * `limit` and `filter` are deliberately NOT required: a card with neither is a
 * perfectly good "how many rows are in this collection" tile.
 *
 * Exported so `WidgetInspector`'s `validateReadmodelWidget` is a delegation rather
 * than a second copy — ticket 26 shipped exactly such a copy as an explicit
 * stand-in, and retiring it is part of this ticket.
 */
export const validateReadmodelBinding = (
  widgetConfig: WidgetDefinition,
  t: TFunction
): ValidationErrors | null => {
  const errors: Record<string, string> = {};

  if ( !widgetConfig.collection || !getReadmodelCollection( widgetConfig.collection )) {
    errors.collection = t(
      'widget_inspector_readmodel_collection_required',
      'Elegí una colección: sin ella la tarjeta no tiene nada que consultar.'
    );
  }

  if ( SEVERITY_WIDGET_TYPES.has( widgetConfig.type ) && !isAlertSeverity( widgetConfig.widgetComponentProps?.severity )) {
    errors.severity = t(
      'widget_inspector_readmodel_severity_required',
      'Elegí una severidad: es el color que el vendedor lee como juicio, y no hay uno seguro por defecto.'
    );
  }

  return Object.keys( errors ).length ? { readmodelSource: errors } : null;
};

// The four READMODEL widgets — TV1-2291 (t25).
//
// `validateCommon` still runs (a widget needs a name, and `name` is REQUIRED on
// `UiWidgetBaseSchema`, so skipping it would hand the aggregate a definition ajv
// rejects); its cube/measures half is skipped for these types. On top of it,
// `validateReadmodelBinding` demands the binding that replaces the Cube one.
//
// Nothing beyond that, for the same reason `table` and `criticalStock` add nothing:
// every remaining prop is optional with a safe default, and the renderers carry
// their own empty / unreadable states for the cases that survive (`ListWidget`
// derives its columns; `ProgressWidget` names the two fields it could not find).
const validateReadmodelWidgetType = ( widgetConfig: WidgetDefinition, t: TFunction ): ValidationErrors | null => {
  const errors: ValidationErrors = {
    ...( validateCommon( widgetConfig, t ) ?? {}),
    ...( validateReadmodelBinding( widgetConfig, t ) ?? {})
  };

  return Object.keys( errors ).length ? errors : null;
};

const validateCount = validateReadmodelWidgetType;
const validateAlert = validateReadmodelWidgetType;
const validateList = validateReadmodelWidgetType;
const validateProgress = validateReadmodelWidgetType;

// Map of validators now accepting t
export const widgetValidators: Record<WidgetType, ( widgetConfig: WidgetDefinition, t: TFunction ) => ValidationErrors | null> = {
  areaChart: validateAreaChart,
  barChart: validateBarChart,
  lineChart: validateLineChart,
  pieChart: validatePieChart,
  donutChart: validateDonutChart,
  scatterChart: validateScatterChart,
  criticalStock: validateCriticalStock,
  table: validateTable,
  kpi: validateKpi,
  text: validateText,
  heading: validateHeading,
  spacer: validateSpacer,
  count: validateCount,
  alert: validateAlert,
  list: validateList,
  progress: validateProgress
};

// Entry
export const validateWebWidget = ( widgetConfig: WidgetDefinition, t: TFunction ): ValidationErrors | null => {
  const { type } = widgetConfig;
  const validator = widgetValidators[type as WidgetType];
  if ( !validator ) {
    return { basic: { type: t( 'widget_builder_validation_invalid_type', 'Invalid widget type: {{type}}', { type }) }};
  }
  return validator( widgetConfig, t );
};
